import { solveChallenge as solveAltchaPow } from "altcha-lib/v1";
import { httpClient } from "@/services/httpClient";
import { logger } from "@/services/loggerService";
/** Backend ALTCHA error codes (byte-exact with `SubmissionErrorCodes` / the R70 map). */
const ALTCHA_ERROR_CODE = {
    unavailable: "Submission.Altcha.Unavailable",
    unsolvable: "Submission.Altcha.Invalid",
};
/**
 * Typed ALTCHA client failure (fail-closed). Carries the backend `errorCode` so the
 * caller routes it through the existing R70 `resolveDonationError` map without
 * re-deriving the message — identical UX to the server-side rejection.
 */
export class AltchaClientError extends Error {
    constructor(kind, cause) {
        super(`ALTCHA ${kind}`);
        Object.defineProperty(this, "kind", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "errorCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The originating error (kept for diagnostics). Declared explicitly because the
         * app targets ES2020, where `Error.cause` and the `ErrorOptions` constructor arg
         * are not part of the standard lib typings.
         */
        Object.defineProperty(this, "cause", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = "AltchaClientError";
        this.kind = kind;
        this.errorCode = ALTCHA_ERROR_CODE[kind];
        this.cause = cause;
    }
}
/**
 * The neutral, configurable challenge endpoint (R83 — no internal service name in a
 * public path). Default `/api/public/altcha/challenge`; the dev proxy + prod edge
 * rewrite it to the altcha service's `/api/altcha/v1/challenge` (see the D-ALTCHA-WIRE
 * seam). Overridable at build time via `VITE_ALTCHA_CHALLENGE_PATH` so the live
 * exposure stays a seam and this task does not block on the (separately-repaired)
 * altcha service.
 *
 * `httpClient` has `baseURL: "/api"`, so the path passed to it is RELATIVE to `/api`
 * (i.e. `/public/altcha/challenge`). We derive that relative form from the full
 * neutral path so the override can be expressed as the full public path.
 */
// TODO(D-ALTCHA-WIRE): the PROD edge (Kong/nginx) exposure of this neutral public
// challenge route -> the altcha service `/api/altcha/v1/challenge` ships with the
// T-PUB-008 infra rewrite (mirrors the SPA nginx `/api` -> `/api/v1` rewrite). The dev
// proxy already maps it; in prod this constant + the edge route are the seam.
const CHALLENGE_PUBLIC_PATH = import.meta.env.VITE_ALTCHA_CHALLENGE_PATH ?? "/api/public/altcha/challenge";
/** Strip the shared `/api` baseURL prefix so the path is correct for `httpClient`. */
const CHALLENGE_REQUEST_PATH = CHALLENGE_PUBLIC_PATH.replace(/^\/api/, "");
/** The submit header carrying the base64 solution (byte-exact with the PHP listener). */
export const ALTCHA_SOLUTION_HEADER = "X-Altcha-Solution";
const ALGORITHMS = ["SHA-1", "SHA-256", "SHA-512"];
function isAlgorithm(value) {
    return typeof value === "string" && ALGORITHMS.includes(value);
}
/**
 * Parse + validate the wire `result` into a typed {@link AltchaChallenge}. A missing
 * or malformed field is a fail-closed `unavailable` reject (a verifier that hands back
 * an unusable challenge is, for our purposes, down — never a silent skip).
 */
function parseChallenge(result) {
    if (!result ||
        !isAlgorithm(result.algorithm) ||
        typeof result.challenge !== "string" ||
        result.challenge === "" ||
        typeof result.salt !== "string" ||
        result.salt === "" ||
        typeof result.signature !== "string" ||
        result.signature === "" ||
        typeof result.maxNumber !== "number" ||
        !Number.isFinite(result.maxNumber) ||
        result.maxNumber < 0) {
        throw new AltchaClientError("unavailable");
    }
    return {
        algorithm: result.algorithm,
        challenge: result.challenge,
        salt: result.salt,
        signature: result.signature,
        maxNumber: result.maxNumber,
    };
}
/**
 * Fetch a server-signed ALTCHA challenge from the configurable neutral endpoint.
 *
 * @throws {AltchaClientError} `unavailable` if the endpoint is unreachable / non-2xx /
 *   returns a malformed envelope (fail-closed — never a silent skip).
 */
export async function fetchChallenge() {
    try {
        const { data } = await httpClient.get(CHALLENGE_REQUEST_PATH);
        return parseChallenge(data?.result);
    }
    catch (e) {
        if (e instanceof AltchaClientError) {
            throw e;
        }
        // Silent catch (the caller surfaces the localized message) -> log per R47.
        logger.error("ALTCHA challenge fetch failed", { kind: "unavailable" });
        throw new AltchaClientError("unavailable", e);
    }
}
/**
 * Solve the proof-of-work: find the integer `n` in `[0..maxNumber]` such that
 * lowercase-hex `SHA-256(salt + n)` equals `challenge` (delegated to `altcha-lib`'s
 * canonical, UI-friendly solver). Runs behind the submit loading state.
 *
 * @throws {AltchaClientError} `unsolvable` if no `n` exists in range.
 */
export async function solveChallenge(challenge) {
    const { promise } = solveAltchaPow(challenge.challenge, challenge.salt, challenge.algorithm, challenge.maxNumber);
    const solution = await promise;
    if (solution === null) {
        // No PoW number in [0..maxNumber] -> a malformed/over-tight challenge. Fail-closed
        // (Invalid analog); log the silent catch per R47, never echo the challenge (R73).
        logger.error("ALTCHA challenge unsolvable within maxNumber", { kind: "unsolvable" });
        throw new AltchaClientError("unsolvable");
    }
    return solution.number;
}
/**
 * Encode the base64 `X-Altcha-Solution` value for a solved challenge. The payload is
 * the classic ALTCHA solution `{ algorithm, challenge, number, salt, signature }` —
 * EXACTLY what `eudonet-opdonationform`'s `HttpAltchaVerifier` forwards to the altcha
 * `/validate` endpoint in payload mode. Field order is irrelevant (the server decodes
 * by key), but kept stable for deterministic test assertions.
 */
export function encodeSolution(challenge, number) {
    const payload = {
        algorithm: challenge.algorithm,
        challenge: challenge.challenge,
        number,
        salt: challenge.salt,
        signature: challenge.signature,
    };
    const json = JSON.stringify(payload);
    // btoa is available in the SPA (DOM) and in the jsdom/happy-dom test env; it expects
    // a binary string — the JSON here is ASCII (hex digests + base64 signature), so a
    // direct btoa is correct without a UTF-8 pre-encode.
    return btoa(json);
}
/**
 * One-shot: fetch a challenge, solve the PoW, and return the base64
 * `X-Altcha-Solution` header value to attach to the submit (T-PUB-005). Fail-closed:
 * any failure throws a typed {@link AltchaClientError} (never returns an empty/absent
 * solution).
 *
 * @returns the base64 solution string for the `X-Altcha-Solution` header.
 * @throws {AltchaClientError} `unavailable` (fetch failed) / `unsolvable` (no PoW).
 */
export async function solveAltcha() {
    const challenge = await fetchChallenge();
    const number = await solveChallenge(challenge);
    return encodeSolution(challenge, number);
}
